import React, { createContext, useContext, useState, useEffect } from 'react';
import { Weights, Opportunity, OpportunityScore } from '../types/opportunity';
import { supabase } from '@/lib/supabase';
import { sampleOpportunities } from '@/data/opportunities';
import { calculateScores } from '@/utils/scoringUtils';

interface AssessmentContextType {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  weights: Weights;
  setWeights: (weights: Weights) => void;
  opportunities: Opportunity[];
  setOpportunities: (opportunities: Opportunity[]) => void;
  scoredOpportunities: OpportunityScore[];
  setScoredOpportunities: (opportunities: OpportunityScore[]) => void;
}

const defaultWeights: Weights = {
  SpeedUpPermitting: 2,
  IncreaseStaffProductivity: 2,
  SimplifyApplicationExperience: 2,
  EnhanceEquitableAccessToServices: 2,
  StrengthenInternalVisibilityDecisionMaking: 2,
  IncreaseProcessTransparency: 2,
  BoostFinancialSustainability: 2,
  CostAndEffort: 2,
  Risk: 2,
  Complexity: 2,
};

const AssessmentContext = createContext<AssessmentContextType | undefined>(undefined);

export const AssessmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [weights, setWeights] = useState<Weights>(defaultWeights);
  const [opportunities] = useState<Opportunity[]>(sampleOpportunities);
  const [scoredOpportunities, setScoredOpportunities] = useState<OpportunityScore[]>([]);

  useEffect(() => {
    const loadSavedAssessment = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        console.log('Checking for saved assessments for user:', user.id);
        const { data: assessments, error } = await supabase
          .from('assessments')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error fetching saved assessment:', error);
          return;
        }

        if (assessments && assessments.length > 0) {
          const savedAssessment = assessments[0];
          console.log('Found saved assessment:', savedAssessment.id);
          
          setWeights(savedAssessment.weights as Weights);
          setScoredOpportunities(savedAssessment.scored_opportunities as OpportunityScore[]);
        } else {
          // If no saved assessment, calculate scores with default weights
          const initialScores = calculateScores(opportunities, defaultWeights);
          setScoredOpportunities(initialScores);
        }
      }
    };

    loadSavedAssessment();
  }, [opportunities]);

  return (
    <AssessmentContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        weights,
        setWeights,
        opportunities,
        setOpportunities: () => {}, // Keeping this for type compatibility
        scoredOpportunities,
        setScoredOpportunities,
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = () => {
  const context = useContext(AssessmentContext);
  if (context === undefined) {
    throw new Error('useAssessment must be used within an AssessmentProvider');
  }
  return context;
};